/**
 * EncuestasPage - Página principal del módulo de Encuestas
 * Features:
 * - Lista de instancias de encuesta
 * - Crear nueva instancia
 * - Participar en encuesta
 * - Ver resultados agregados
 * - Estado de participación
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  FileText, 
  BarChart3, 
  Calendar, 
  Users, 
  CheckCircle,
  Clock,
  TrendingUp,
  Lock
} from 'lucide-react';
import { encuestasAPI, organizacionesAPI } from '../../api';
import useAuthStore from '../../store/authStore';
import {
  Button,
  Card,
  Table,
  Pagination,
  Loading,
  Alert,
  Modal,
} from '../../components/common';
import EncuestaForm from '../../components/encuestas/EncuestaForm';
import EncuestaResults from '../../components/encuestas/EncuestaResults';

const EncuestasPage = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();

  // Estado
  const [instancias, setInstancias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  // Filtros
  const [filters, setFilters] = useState({
    activa: '',
    fecha_desde: '',
    fecha_hasta: '',
  });

  // Modales
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [selectedInstancia, setSelectedInstancia] = useState(null);
  const [organizaciones, setOrganizaciones] = useState([]);

  // Formulario de crear instancia
  const [createForm, setCreateForm] = useState({
    id_organizacion: '',
    titulo: '',
    descripcion: '',
    fecha_cierre: '',
    anonima: true,
  });

  // Cargar instancias
  const loadInstancias = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
      };

      // Aplicar filtros
      if (filters.activa !== '') {
        params.activa = filters.activa === 'true';
      }
      if (filters.fecha_desde) {
        params.fecha_desde = filters.fecha_desde;
      }
      if (filters.fecha_hasta) {
        params.fecha_hasta = filters.fecha_hasta;
      }

      const response = await encuestasAPI.getInstancias(params);
      
      // El endpoint retorna un array directo, no {items: [...]}
      const data = Array.isArray(response.data) ? response.data : (response.data.items || []);
      setInstancias(data);
      setTotalItems(response.data.total || data.length);
      setTotalPages(Math.ceil((response.data.total || data.length) / itemsPerPage));
    } catch (err) {
      console.error('Error al cargar instancias:', err);
      setError(err.message || 'Error al cargar instancias');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInstancias();
  }, [currentPage, filters]);

  useEffect(() => {
    if (user?.rol === 'superadmin') {
      organizacionesAPI.getAll({ limit: 100, activo: true })
        .then(res => setOrganizaciones(res.data.items || []))
        .catch(err => console.error('Error cargando organizaciones:', err));
    }
  }, [user]);

  // Handlers
  const handleCreateInstancia = async (e) => {
    e.preventDefault();
    
    try {
      setError(null);
      const orgId = user.rol === 'superadmin'
        ? parseInt(createForm.id_organizacion, 10)
        : parseInt(user.id_organizacion, 10);
      if (!orgId) {
        setError('Debe seleccionar una organización.');
        return;
      }
      const payload = {
        id_organizacion: orgId,
        titulo: createForm.titulo,
        descripcion: createForm.descripcion || null,
        fecha_cierre: createForm.fecha_cierre ? new Date(createForm.fecha_cierre).toISOString() : null,
        anonima: createForm.anonima,
      };
      await encuestasAPI.createInstancia(payload);
      setSuccess('Instancia de encuesta creada correctamente');
      setShowCreateModal(false);
      setCreateForm({
        id_organizacion: '',
        titulo: '',
        descripcion: '',
        fecha_cierre: '',
        anonima: true,
      });
      loadInstancias();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error al crear instancia:', err);
      setError(err.response?.data?.detail || err.message || 'Error al crear instancia');
    }
  };

  const handleParticipar = (instancia) => {
    // Verificar si ya participó
    if (instancia.ya_participo) {
      setError('Ya has participado en esta encuesta');
      setTimeout(() => setError(null), 3000);
      return;
    }

    // Verificar si está activa
    if (!instancia.activa) {
      setError('Esta encuesta ya está cerrada');
      setTimeout(() => setError(null), 3000);
      return;
    }

    setSelectedInstancia(instancia);
    setShowFormModal(true);
  };

  const handleVerResultados = (instancia) => {
    setSelectedInstancia(instancia);
    setShowResultsModal(true);
  };

  const handleRespuestaSubmitted = () => {
    setShowFormModal(false);
    setSuccess('Respuestas enviadas correctamente');
    loadInstancias();
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleFilterChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters });
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      activa: '',
      fecha_desde: '',
      fecha_hasta: '',
    });
    setCurrentPage(1);
  };

  // Calcular nivel ético promedio
  const getNivelEtico = (instancia) => {
    if (!instancia?.nivel_etico_promedio) return null;
    
    const nivel = instancia.nivel_etico_promedio;
    
    if (nivel === 'Alto') return { nivel: 'Alto', color: 'green', icon: '🟢' };
    if (nivel === 'Medio') return { nivel: 'Medio', color: 'yellow', icon: '🟡' };
    if (nivel === 'Bajo') return { nivel: 'Bajo', color: 'orange', icon: '🟠' };
    if (nivel === 'Crítico') return { nivel: 'Crítico', color: 'red', icon: '🔴' };
    
    return null;
  };

  // Helper: verificar si instancia está activa (usa campos reales del backend)
  const isActiva = (item) => {
    if (!item) return false;
    const ahora = new Date();
    return item.activa && (!item.fecha_cierre || ahora <= new Date(item.fecha_cierre));
  };

  // Columnas de la tabla
  const columns = [
    {
      key: 'titulo',
      label: 'Título',
      render: (_value, item) => {
        if (!item) return null;
        return (
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-gray-400" />
            <div>
              <span className="font-medium text-gray-900">{item.titulo}</span>
              {item.descripcion && (
                <p className="text-sm text-gray-500 truncate max-w-xs">
                  {item.descripcion}
                </p>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: 'fechas',
      label: 'Período',
      render: (_value, item) => {
        if (!item) return null;
        return (
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-400" />
            <div className="text-sm">
              <div>
                {item.fecha_apertura
                  ? new Date(item.fecha_apertura).toLocaleDateString('es-ES')
                  : '-'}
              </div>
              <div className="text-gray-500">
                {item.fecha_cierre
                  ? new Date(item.fecha_cierre).toLocaleDateString('es-ES')
                  : 'Sin fecha de cierre'}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      key: 'participacion',
      label: 'Participación',
      render: (_value, item) => {
        if (!item) return null;
        const total = item.total_participantes || 0;
        const participaron = item.participantes_completados || 0;
        const porcentaje = total > 0 ? Math.round((participaron / total) * 100) : 0;
        
        return (
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-gray-400" />
            <div>
              <div className="font-medium">
                {participaron}/{total}
              </div>
              <div className="text-xs text-gray-500">{porcentaje}%</div>
            </div>
          </div>
        );
      },
    },
    {
      key: 'estado',
      label: 'Estado',
      render: (_value, item) => {
        if (!item) return null;
        const activa = isActiva(item);
        
        return (
          <div className="flex flex-col gap-1">
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                activa
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-800'
              }`}
            >
              {activa ? (
                <>
                  <Clock className="h-3 w-3 mr-1" />
                  Activa
                </>
              ) : (
                <>
                  <Lock className="h-3 w-3 mr-1" />
                  Cerrada
                </>
              )}
            </span>
            {item.ya_participo && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                <CheckCircle className="h-3 w-3 mr-1" />
                Participaste
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'nivel',
      label: 'Nivel Ético',
      render: (_value, item) => {
        if (!item) return null;
        const nivel = getNivelEtico(item);
        if (!nivel) {
          return <span className="text-gray-400">-</span>;
        }
        return (
          <div className="flex items-center gap-2">
            <span>{nivel.icon}</span>
            <span
              className={`font-medium ${
                nivel.color === 'red'
                  ? 'text-red-600'
                  : nivel.color === 'orange'
                  ? 'text-orange-600'
                  : nivel.color === 'yellow'
                  ? 'text-yellow-600'
                  : 'text-green-600'
              }`}
            >
              {nivel.nivel}
            </span>
          </div>
        );
      },
    },
    {
      key: 'acciones',
      label: 'Acciones',
      render: (_value, item) => {
        if (!item) return null;
        const activa = isActiva(item);
        
        return (
          <div className="flex gap-2">
            {activa && !item.ya_participo && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleParticipar(item)}
              >
                Participar
              </Button>
            )}
            {(item.participantes_completados > 0 || item.ya_participo) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleVerResultados(item)}
              >
                <BarChart3 className="h-4 w-4" />
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Encuestas</h1>
          <p className="mt-1 text-sm text-gray-500">
            Evaluación ética mediante encuestas participativas
          </p>
        </div>
        {(user?.rol === 'superadmin' || user?.rol === 'admin_org') && (
          <Button
            variant="primary"
            onClick={() => setShowCreateModal(true)}
            icon={Plus}
          >
            Nueva Instancia
          </Button>
        )}
      </div>

      {/* Alertas */}
      {error && (
        <Alert
          type="error"
          title="Error"
          message={error}
          onClose={() => setError(null)}
        />
      )}

      {success && (
        <Alert
          type="success"
          title="Éxito"
          message={success}
          onClose={() => setSuccess(null)}
        />
      )}

      {/* Estadísticas rápidas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Instancias</p>
              <p className="text-2xl font-bold">{totalItems}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <Clock className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Activas</p>
              <p className="text-2xl font-bold">
                {instancias.filter((i) => i?.activa).length}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Users className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Participaciones</p>
              <p className="text-2xl font-bold">
                {instancias.reduce((acc, i) => acc + (i?.participantes_completados || 0), 0)}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filtros */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estado
            </label>
            <select
              value={filters.activa}
              onChange={(e) => handleFilterChange({ activa: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Todas</option>
              <option value="true">Activas</option>
              <option value="false">Cerradas</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Desde
            </label>
            <input
              type="date"
              value={filters.fecha_desde}
              onChange={(e) => handleFilterChange({ fecha_desde: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Hasta
            </label>
            <input
              type="date"
              value={filters.fecha_hasta}
              onChange={(e) => handleFilterChange({ fecha_hasta: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="flex items-end">
            <Button variant="outline" onClick={clearFilters} className="w-full">
              Limpiar Filtros
            </Button>
          </div>
        </div>
      </Card>

      {/* Tabla de instancias */}
      <Card>
        {loading ? (
          <div className="p-8">
            <Loading size="lg" text="Cargando encuestas..." />
          </div>
        ) : instancias.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No hay instancias de encuesta</p>
            {(user?.rol === 'superadmin' || user?.rol === 'admin_org') && (
              <Button
                variant="primary"
                onClick={() => setShowCreateModal(true)}
                className="mt-4"
              >
                Crear Primera Instancia
              </Button>
            )}
          </div>
        ) : (
          <>
            <Table columns={columns} data={instancias} />
            {totalPages > 1 && (
              <div className="border-t p-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </>
        )}
      </Card>

      {/* Modal: Crear Instancia */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Nueva Instancia de Encuesta"
        size="lg"
      >
        <form onSubmit={handleCreateInstancia} className="space-y-4">
          {user?.rol === 'superadmin' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Organización *
              </label>
              <select
                value={createForm.id_organizacion}
                onChange={(e) => setCreateForm({ ...createForm, id_organizacion: e.target.value })}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Seleccione una organización</option>
                {organizaciones.map((org) => (
                  <option key={org.id_organizacion} value={org.id_organizacion}>
                    {org.nombre}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Título *
            </label>
            <input
              type="text"
              value={createForm.titulo}
              onChange={(e) => setCreateForm({ ...createForm, titulo: e.target.value })}
              required
              maxLength={255}
              placeholder="Ej: Encuesta Ética Q4 2025"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descripción
            </label>
            <textarea
              value={createForm.descripcion}
              onChange={(e) => setCreateForm({ ...createForm, descripcion: e.target.value })}
              rows={3}
              placeholder="Descripción de la encuesta..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha de Cierre
              </label>
              <input
                type="date"
                value={createForm.fecha_cierre}
                onChange={(e) => setCreateForm({ ...createForm, fecha_cierre: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer pb-2">
                <input
                  type="checkbox"
                  checked={createForm.anonima}
                  onChange={(e) => setCreateForm({ ...createForm, anonima: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-gray-700">Encuesta anónima</span>
              </label>
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowCreateModal(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" variant="primary">
              Crear Instancia
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal: Formulario de Encuesta */}
      {selectedInstancia && (
        <Modal
          isOpen={showFormModal}
          onClose={() => {
            setShowFormModal(false);
            setSelectedInstancia(null);
          }}
          title={`Participar: ${selectedInstancia.titulo}`}
          size="xl"
        >
          <EncuestaForm
            instanciaId={selectedInstancia.id_instancia}
            onSubmit={handleRespuestaSubmitted}
            onCancel={() => {
              setShowFormModal(false);
              setSelectedInstancia(null);
            }}
          />
        </Modal>
      )}

      {/* Modal: Resultados */}
      {selectedInstancia && (
        <Modal
          isOpen={showResultsModal}
          onClose={() => {
            setShowResultsModal(false);
            setSelectedInstancia(null);
          }}
          title={`Resultados: ${selectedInstancia.titulo}`}
          size="xl"
        >
          <EncuestaResults instanciaId={selectedInstancia.id_instancia} />
        </Modal>
      )}
    </div>
  );
};

export default EncuestasPage;
